import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Wrench, CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

export default function BookingRequest() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get("action") === "decline" ? "decline" : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [request, setRequest] = useState(null);
  const [mode, setMode] = useState(initialAction); // null | "approve" | "decline"
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // "approved" | "declined"
  const [resultError, setResultError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/booking/request/${token}`);
        if (!alive) return;
        setRequest(res.data);
      } catch (e) {
        if (!alive) return;
        setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const doApprove = async () => {
    setSubmitting(true);
    setResultError("");
    try {
      await api.post(`/booking/request/${token}/approve`);
      setResult("approved");
    } catch (e) {
      setResultError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const doDecline = async () => {
    setSubmitting(true);
    setResultError("");
    try {
      await api.post(`/booking/request/${token}/decline`, { reason });
      setResult("declined");
    } catch (e) {
      setResultError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-xl text-slate-900">
              Bell Appliance Repair
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">
              Booking Request
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10" data-testid="booking-request-page">
        {loading && (
          <div className="flex items-center gap-2 text-slate-600" data-testid="request-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading request…
          </div>
        )}

        {!loading && error && (
          <Card className="border-red-200" data-testid="request-error">
            <CardContent className="py-10 text-center space-y-3">
              <XCircle className="w-10 h-10 text-red-500 mx-auto" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Link invalid</h2>
              <p className="text-slate-600">{error}</p>
              <p className="text-xs text-slate-500">
                This can happen if the request was already handled or the link expired.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && request && result === "approved" && (
          <Card className="border-emerald-200" data-testid="approved-panel">
            <CardContent className="py-10 text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Appointment approved
              </h2>
              <p className="text-slate-600">
                The customer has been notified by email{request?.customer?.phone ? " and SMS" : ""}.
                The ticket is now on your schedule as{" "}
                <strong>{request.appt_date}</strong> · {request.slot_label}.
              </p>
              <Link to="/" className="inline-block text-blue-700 text-sm hover:underline">
                Go to dashboard →
              </Link>
            </CardContent>
          </Card>
        )}

        {!loading && request && result === "declined" && (
          <Card className="border-slate-200" data-testid="declined-panel">
            <CardContent className="py-10 text-center space-y-3">
              <XCircle className="w-12 h-12 text-slate-500 mx-auto" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Request declined</h2>
              <p className="text-slate-600">
                The customer has been emailed a polite note with a link to pick a different time.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && request && !result && request.status !== "pending_approval" && (
          <Card className="border-amber-200" data-testid="already-handled">
            <CardContent className="py-10 text-center space-y-3">
              <Clock className="w-10 h-10 text-amber-600 mx-auto" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Already {request.status.replace("_", " ")}
              </h2>
              <p className="text-slate-600">
                This request was already handled. No further action needed.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && request && !result && request.status === "pending_approval" && (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-8 space-y-6">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Pending request · #{request.confirmation}
                </div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 mt-1">
                  {request.customer.first_name} {request.customer.last_name}
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <Row label="Requested time">
                  <div className="font-semibold">{request.appt_date}</div>
                  <div className="text-slate-600">{request.slot_label}</div>
                </Row>
                <Row label="Phone">{request.customer.phone || "—"}</Row>
                <Row label="Email">{request.customer.email || "—"}</Row>
                <Row label="Appliance">
                  {(request.appliance_type || "").replace(/^./, (c) => c.toUpperCase())} ·{" "}
                  {(request.manufacturer || "").toUpperCase()}
                </Row>
                <Row label="Address" full>
                  {[
                    request.customer.street,
                    request.customer.city,
                    request.customer.state,
                    request.customer.zip_code,
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </Row>
                <Row label="Problem" full>
                  {request.job_description || "—"}
                </Row>
              </div>

              {resultError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="request-action-error">
                  {resultError}
                </div>
              )}

              {mode !== "decline" && (
                <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-slate-100">
                  <Button
                    onClick={doApprove}
                    disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                    data-testid="approve-request-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Approve
                  </Button>
                  <Button
                    onClick={() => setMode("decline")}
                    variant="outline"
                    disabled={submitting}
                    className="flex-1"
                    data-testid="show-decline-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Decline
                  </Button>
                </div>
              )}

              {mode === "decline" && (
                <div className="space-y-3 pt-2 border-t border-slate-100" data-testid="decline-panel">
                  <label className="text-sm font-medium text-slate-700">
                    Reason (optional — included in customer email)
                  </label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Fully booked that day, or out of service area."
                    className="min-h-[80px]"
                    data-testid="decline-reason"
                  />
                  <div className="flex gap-3">
                    <Button
                      onClick={doDecline}
                      disabled={submitting}
                      className="bg-red-600 hover:bg-red-700 text-white flex-1"
                      data-testid="confirm-decline-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4 mr-2" />
                      )}
                      Send decline
                    </Button>
                    <Button
                      onClick={() => setMode(null)}
                      variant="outline"
                      disabled={submitting}
                      className="flex-1"
                      data-testid="cancel-decline-btn"
                    >
                      Back
                    </Button>
                  </div>
                </div>
              )}

              <p className="text-xs text-slate-500 pt-2">
                Auto-declines 72 hours after request. The customer is only notified after your decision.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function Row({ label, children, full = false }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}
